import {
  Autocomplete,
  Box,
  CircularProgress,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@benchlabs/react-design-system';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { QueryClient, QueryClientProvider } from 'react-query';
import React, { useEffect, useState } from 'react';
import { useFetchDataSources } from './api/useFetchDataSources';
import { useFetchGlobalService } from './api/useFetchGlobalService';
import { useFetchGlobalServicesForDataSource } from './api/useFetchGlobalServicesForDataSource';
import { findGlobalServicesStyles } from './FindGlobalServices.styles';
import { SplitFeatureFlags } from '../../../../features';
import { DataSource } from './types/DataSource';
import { GlobalService } from './types/GlobalService';

interface FindGlobalServicesProps {
  globalServiceId?: string;
  onGlobalServiceSelected: (globalService: GlobalService) => void;
  // Optional props for testing/storybook
  dataSources?: DataSource[];
  globalServices?: GlobalService[];
  loading?: boolean;
}

const queryClient = new QueryClient(); // move to global area when we convert over more MFEs

export const FindGlobalServices = (props: FindGlobalServicesProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <FindGlobalServicesContent {...props} />
    </QueryClientProvider>
  );
};

const FindGlobalServicesContent = ({
  globalServiceId,
  onGlobalServiceSelected,
  dataSources,
  globalServices,
  loading,
}: FindGlobalServicesProps) => {
  const [dataSourceValue, setDataSourceValue] = useState<DataSource | null>(null);
  const [actionValue, setActionValue] = useState<GlobalService | null>(null);

  const [dataSourceError, setDataSourceError] = useState(false);
  const [actionError, setActionError] = useState(false);

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;
  const useGraphQL =
    features[SplitFeatureFlags.USE_NEW_BENCH_SERVICES]?.value === FeatureValue.FEATURE_ON;

  const { data: globalService, status: fetchGlobalServiceStatus } = useFetchGlobalService({
    globalServiceId,
    useGraphQL,
  });

  const { data: dataSourcesData, status: fetchDataSourcesStatus } = useFetchDataSources({
    useGraphQL,
  });

  const { data: globalServicesData, status: fetchGlobalServicesStatus } =
    useFetchGlobalServicesForDataSource({
      dataSourceId: dataSourceValue?.dataSourceId,
      useGraphQL,
    });

  useEffect(() => {
    if (globalService && dataSourcesData?.length) {
      const globalServiceDataSource = {
        dataSourceId: globalService.dataSourceId,
        name: globalService.dataSourceName ?? '',
      };
      setDataSourceValue(globalServiceDataSource);
      setActionValue(globalService);
      onGlobalServiceSelected(globalService);
    }
  }, [globalService, dataSourcesData, onGlobalServiceSelected]);

  //NOTE - This is here to allow for the dataSources and globalServices to be passed in as props for testing on Storybook
  const dataSourceOptions = dataSourcesData ?? dataSources;
  const globalServicesOptions = globalServicesData ?? globalServices;

  const handleDataSourceError = (value: DataSource | null) => {
    setDataSourceError(value === null);
  };

  const handleActionError = (value: GlobalService | null) => {
    setActionError(value === null);
  };

  const handleDataSourceChanged = (
    _: React.SyntheticEvent,
    newDataSourceValue: DataSource | null,
  ) => {
    setDataSourceValue(newDataSourceValue);
    actionValue && setActionValue(null);
    handleDataSourceError(newDataSourceValue);
  };

  const handleActionChanged = (_: React.SyntheticEvent, newActionValue: GlobalService | null) => {
    setActionValue(newActionValue);
    newActionValue && onGlobalServiceSelected(newActionValue);
    handleActionError(newActionValue);
  };

  function getNoOptionsTextForDataSources() {
    if (!dataSourcesData?.length) return 'No data sources found.';
    return `Data source '${dataSourceValue?.name ?? ''}' is not supported. Select a data source.`;
  }

  const loadingData =
    loading ??
    [fetchDataSourcesStatus, fetchGlobalServiceStatus, fetchGlobalServicesStatus].includes(
      'loading',
    );

  return (
    <Box sx={findGlobalServicesStyles}>
      <FormLabel id="select-data-source-and-action"></FormLabel>
      <Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr minmax(50px, 279px)',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography component="label" variant="body2" htmlFor="select-data-source">
            DATA SOURCE
          </Typography>
          <Autocomplete
            id="select-data-source"
            options={dataSourceOptions ?? []}
            getOptionLabel={(option: DataSource) => option.name}
            isOptionEqualToValue={(option, value) => option.dataSourceId === value.dataSourceId}
            noOptionsText={getNoOptionsTextForDataSources()}
            value={dataSourceValue}
            onChange={handleDataSourceChanged}
            size="small"
            sx={{
              '& .MuiAutocomplete-endAdornment': {
                width: 'auto',
              },
            }}
            ListboxProps={{
              sx: {
                fontSize: '0.8rem',
              },
            }}
            loading={loadingData}
            renderInput={params => (
              <TextField
                required
                error={dataSourceError}
                helperText={dataSourceError ? 'Required. Select a data source.' : ''}
                {...params}
                size="small"
                disabled={loadingData}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingData ? <CircularProgress color="inherit" size={20} /> : null}{' '}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  style: { fontSize: '0.8rem' },
                }}
              />
            )}
          ></Autocomplete>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr minmax(50px, 279px)',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography component="label" variant="body2" htmlFor="select-action">
            ACTION
          </Typography>
          <Autocomplete
            id="select-action"
            disabled={!dataSourceValue}
            value={actionValue}
            onChange={handleActionChanged}
            options={dataSourceValue && globalServicesOptions ? globalServicesOptions : []}
            getOptionLabel={(option: GlobalService) => option.actionName ?? ''}
            isOptionEqualToValue={(option, value) =>
              option.globalServiceId === value.globalServiceId
            }
            noOptionsText={`Action '${
              actionValue?.actionName ?? ''
            }' is not supported. Select an action.`}
            ListboxProps={{
              sx: {
                fontSize: '0.8rem',
              },
            }}
            loading={loadingData}
            renderInput={params => (
              <TextField
                required
                error={actionError}
                helperText={actionError ? 'Required. Select an action.' : ''}
                {...params}
                size="small"
                disabled={loadingData}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingData ? <CircularProgress color="inherit" size={20} /> : null}{' '}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  style: { fontSize: '0.8rem' },
                }}
              />
            )}
          ></Autocomplete>
        </Box>
      </Stack>
    </Box>
  );
};
